import React, { FC, useMemo } from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import { GreenButton, RedButton } from "./ColorButtons";

import { Field, Form } from "react-final-form";
import { TextField } from "final-form-material-ui";
import { FormApi } from "final-form";

import Autocomplete from "@material-ui/lab/Autocomplete";
import MTextField from "@material-ui/core/TextField";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      height: "calc(100dvh - 100px)",
      width: "100%",
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(3),
      fontSize: theme.typography.body2.fontSize,
    },
    formWrapper: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      fontSize: theme.typography.body2.fontSize,
    },
    formActions: {
      display: "flex",
      flexDirection: "row",
      gap: "0.25rem",
      justifyContent: "flex-end",
    },
    formRow: {
      display: "flex",
      flexDirection: "row",
      gap: theme.spacing(2),
      "& label": {
        display: "flex",
        width: "150px",
        justifyContent: "right",
        alignItems: "center",
      },
      "& div:first-child": {
        width: "350px",
      },
    },
  });

export enum FieldTypes {
  "Text" = "Text",
  "Select" = "Select",
}

type FieldData = {
  display: string;
  value: any;
};

type Props = {
  initValues: any;
  fields: { title: string; field: string; type: FieldTypes; data?: FieldData[]; disabled?: boolean }[];
  onClose(): void;
  onSubmit(values: any): void;
} & WithStyles<typeof styles>;

const AgileFormUnstyled: FC<Props> = ({ classes, fields, initValues, onClose, onSubmit }) => {
  if (!initValues) return <></>;

  return (
    <Form
      initialValues={initValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, form }) => (
        <form onSubmit={handleSubmit} className={classes.form}>
          <div className={classes.formWrapper} style={{ marginTop: "1rem" }}>
            {fields.map((field) =>
              field.type === FieldTypes.Text ? (
                <FormTextField classes={classes} field={field.field} title={field.title} disabled={field.disabled} />
              ) : field.type === FieldTypes.Select ? (
                <CustomSelect classes={classes} field={field.field} title={field.title} data={field.data} form={form} disabled={field.disabled} />
              ) : (
                <></>
              ),
            )}
          </div>
          <div className={classes.formActions}>
            <RedButton variant="contained" type="button" onClick={() => onClose()} disabled={form.getState().submitting}>
              Close
            </RedButton>
            <GreenButton variant="contained" type="submit" disabled={form.getState().submitting}>
              Save and Close
            </GreenButton>
          </div>
        </form>
      )}
    />
  );
};

type FormTextFieldProps = {
  title: string;
  field: string;
  disabled?: boolean;
} & WithStyles<typeof styles>;

export const FormTextField: FC<FormTextFieldProps> = ({ title, field, classes, disabled = false }) => {
  return (
    <div className={classes.formRow}>
      <label>{title}:</label>
      <div>
        <Field name={field} type="text" fullWidth={true} component={TextField} disabled={disabled} />
      </div>
    </div>
  );
};

type SelectType = { display: string; value: any };
type CustomSelectProps = {
  form: FormApi;
  field: string;
  title: string;
  data: SelectType[];
  disabled?: boolean;
  onParse?(value: any): void;
} & WithStyles<typeof styles>;

const CustomSelect: React.FC<CustomSelectProps> = (props) => {
  const { classes, field, data, title, form, onParse, disabled = false } = props;

  const selected = useMemo(() => data.find((item) => item.value === form.getState().values[field]), [data, form, field]);

  const handleItemSelect = (_: unknown, data: any) => {
    const value = data.value;
    form.change(field, value);
    if (onParse) {
      onParse(value);
    }
  };

  return (
    <div className={classes.formRow}>
      <label>{title}:</label>
      <div>
        <Field
          name={field}
          label={title}
          render={(props) => (
            <Autocomplete
              {...props}
              id={field}
              fullWidth
              options={data}
              value={selected || undefined}
              disabled={disabled}
              onChange={handleItemSelect}
              getOptionLabel={(option: any) => option.display}
              renderInput={(params) => <MTextField {...params} variant="standard" />}
            />
          )}
        />
      </div>
    </div>
  );
};

export default withStyles(styles)(AgileFormUnstyled);
